import { SkeletonMemberList } from "@/components/Skeletons";
import { useGetChat } from "@/hooks/chat/useGetChat";
import { SocketManager } from "@/services";
import useChatStore from "@/store/chatStore";
import { CHAT_ROLE, COMMON_STATUS, SOCKET_EVENTS } from "@/types";
import React, { useCallback, useEffect, useState } from "react";
import { BsPlus } from "react-icons/bs";
import { UserAvatar } from "../../components/UserAvatar";
import ModalAddMember from "./ModalAddMember";
import { IoArrowBackOutline } from "react-icons/io5";
import { cn } from "@/utils";

interface Props {
  roomId: number;
  hiddenSideBar?: () => void;
}

const MemberList: React.FC<Props> = ({ roomId, hiddenSideBar }) => {
  const { memberNumber } = useChatStore();
  const isConnectionReady = useChatStore((state) => state.isConnectionReady);

  // State to show modal to add member
  const [openAddMember, setOpenAddMember] = useState(false);

  // a object store list of email of users in room
  const [userEmails, setUserEmails] = useState<Record<string, boolean>>({});

  const { chat, isLoading } = useGetChat(roomId);

  const chatRole = useChatStore((state) => state.chatRole);

  // Set object joined users' emails
  const handleUserInRooms = (emails: { email: string }[]) => {
    const emailStatus: Record<string, boolean> = {};
    emails?.forEach((email) => {
      emailStatus[email.email] = true;
    });
    setUserEmails(emailStatus);
  };

  // Handle user joined event
  const handleUserJoined = (emails: { email: string }[]) => {
    setUserEmails((prev) => {
      const updated = { ...prev };
      emails.forEach((email) => {
        updated[email.email] = true;
      });
      return updated;
    });
  };

  const handleUserLeft = (emails: { email: string }[]) => {
    setUserEmails((prev) => {
      const updated = { ...prev };
      emails.forEach((email) => {
        delete updated[email.email];
      });
      return updated;
    });
  };

  // Listen event get users in room socket.io to check online status
  useEffect(() => {
    if (!isConnectionReady) return;

    const socketManager = SocketManager.getInstance();
    // Emit get users in room event
    socketManager.emitGetUsersInRoom({ roomId: roomId.toString() });

    // Listen user joined event
    socketManager.onUserJoined((data) => {
      // Handle online status update here if needed
      handleUserJoined(Array.isArray(data) ? data : [data]);
    });

    // List event users_in_room after emit get users in room
    socketManager.onUsersInRoom((data) => {
      handleUserInRooms(Array.isArray(data) ? data : [data]);
    });

    // Listen event user_left
    socketManager.onUserLeft((data) => {
      handleUserLeft(Array.isArray(data) ? data : [data]);
    });

    // Cleanup function to remove listener on unmount
    return () => {
      // No specific cleanup needed here as useGetChat handles socket listeners
      // Tránh bị duplicate listeners khi component unmount và mount lại
      socketManager.off(SOCKET_EVENTS.USER_JOINED);
      socketManager.off(SOCKET_EVENTS.USERS_IN_ROOM);
      socketManager.off(SOCKET_EVENTS.USER_LEFT);
    };
  }, [roomId, isConnectionReady]);

  // open modal to add member
  const handleOpenAddMember = () => {
    setOpenAddMember(true);
  };

  // get user status
  const getUserStatus = useCallback(
    (
      isOnline: boolean,
      status?: COMMON_STATUS
    ): "online" | "disabled" | undefined => {
      if (status === COMMON_STATUS.DISABLE) return "disabled";
      if (isOnline) return "online";
    },
    []
  );

  const AddUserButon = ({ className }: { className?: string }) => {
    return chatRole === CHAT_ROLE.INCHARGE ? (
      <button
        className={cn("flex items-center text-cyan justify-center w-full", className)}
        onClick={handleOpenAddMember}
      >
        <BsPlus className="size-6" />
        <span>メンバーを追加</span>
      </button>
    ) : null;
  };

  if (isLoading) return <SkeletonMemberList type="account" />;

  const chatUsers = chat?.chatUsers;

  return (
    <section>
      <div className="px-4 py-2 flex gap-2 items-center xl:hidden">
        <div className="w-fit flex gap-2 items-center" onClick={hiddenSideBar}>
          <IoArrowBackOutline size={16} color="#637381" className="w-5 h-5" />
          <p className="text-sm text-light_primary font-semibold leading-5.5">
            {`ルーム（${memberNumber}）`}
          </p>
        </div>
        {/* Set permission only for admin and creator to add member  */}
        <AddUserButon className="flex xl:hidden !ml-auto w-fit leading-5.5" />
      </div>

      <div className="p-4 space-y-4">
        {chatUsers?.map((userItem) => {
          const user = userItem.user;
          if (!user) return null;

          return (
            <div key={user.id} className="mb-2 flex items-center gap-4">
              {/* Avatar user  */}
              <UserAvatar
                url={user?.image}
                status={getUserStatus(!!userEmails[user.email], user.status)}
              />

              <div className="flex-1 text-sm leading-5.5 overflow-hidden">
                {/* Name user  */}
                <div
                  className="text-light_primary truncate overflow-hidden"
                  title={user.email}
                >
                  {user?.full_name || user?.email}
                </div>

                {/* Type user  */}
                <div className="text-light_secondary truncate">
                  {/* admin in charge  */}
                  {chat?.pic?.id === userItem.user_id && "TFTC担当者"}
                  {/* normal member  */}
                  {chat?.pic?.id !== userItem.user_id &&
                    userItem.user_id !== chat?.created_by?.id &&
                    "メンバー"}
                  {/* creator  */}
                  {userItem.user_id === chat?.created_by?.id && "チャット作成者"}
                </div>
                <div className="text-light_secondary text-xs">
                  {user.status === COMMON_STATUS.DISABLE &&
                    // User disabled
                    "(無効アカウント)"}
                </div>
              </div>
            </div>
          );
        })}

        {/* Set permission only for admin and creator to add member  */}
        <AddUserButon className="hidden xl:flex" />
      </div>

      <ModalAddMember
        open={openAddMember}
        onOpenChange={setOpenAddMember}
        roomId={roomId}
      />
    </section>
  );
};

export default MemberList;
